/**
 * PieceContext — the right-hand context column for the reading experience.
 *
 * Two stacked sections, each of which omits itself when its array is empty:
 *   - ENTITIES IN THIS PIECE: the graph hook. Clickable entity cards that
 *     deep-link into the entity dossier (/tags/[slug]).
 *   - MORE ON THESE: recent pieces touching the same entities, ranked by the
 *     host (recency, NOT claps).
 *
 * Presentational only — the host page resolves entities and computes `more`.
 * Shared between the article reader (Stage 1) and breaking-news reader
 * (Stage 2). On mobile the parent grid stacks this column below the body.
 */

'use client';

import React from 'react';
import Link from 'next/link';
import { entityUrl } from '@/lib/entity-url';

export interface PieceContextEntity {
  slug: string;
  name: string;
  image?: string;
  type?: string;
}

export interface PieceContextMore {
  href: string;
  title: string;
  eyebrow?: string;
}

interface PieceContextProps {
  entities: PieceContextEntity[];
  more: PieceContextMore[];
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-scout-gold">
      {children}
    </div>
  );
}

function EntityMonogram({ name }: { name: string }) {
  const initial = (name?.trim()?.[0] || '?').toUpperCase();
  return (
    <span className="flex h-9 w-9 flex-none items-center justify-center rounded border-[0.5px] border-divider bg-surface2 font-mono text-sm font-semibold text-parchment-dim">
      {initial}
    </span>
  );
}

export default function PieceContext({ entities, more }: PieceContextProps) {
  const hasEntities = entities && entities.length > 0;
  const hasMore = more && more.length > 0;

  if (!hasEntities && !hasMore) return null;

  return (
    <div className="space-y-10">
      {hasEntities && (
        <section>
          <SectionHeading>Entities in this piece</SectionHeading>
          <div className="space-y-1.5">
            {entities.map((entity) => (
              <Link
                key={entity.slug}
                href={entityUrl(entity.slug)}
                className="group flex items-center gap-3 rounded border-[0.5px] border-divider bg-surface px-3 py-2.5 transition-colors hover:border-scout-gold/50"
              >
                {entity.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={entity.image}
                    alt=""
                    className="h-9 w-9 flex-none rounded border-[0.5px] border-divider object-cover"
                  />
                ) : (
                  <EntityMonogram name={entity.name} />
                )}
                <span className="min-w-0">
                  <span className="block truncate font-body text-sm font-medium text-parchment transition-colors group-hover:text-scout-gold">
                    {entity.name}
                  </span>
                  {entity.type && (
                    <span className="block truncate font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim">
                      {entity.type}
                    </span>
                  )}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {hasMore && (
        <section>
          <SectionHeading>More on these</SectionHeading>
          <div className="space-y-1">
            {more.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group block rounded px-3 py-2.5 transition-colors hover:bg-surface"
              >
                <span className="block font-body text-sm leading-snug text-parchment transition-colors group-hover:text-scout-gold">
                  {item.title}
                </span>
                {item.eyebrow && (
                  <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.12em] text-parchment-dim">
                    {item.eyebrow}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
